import { computed, ref } from 'vue';
import {
  AgentChatError,
  type AgentChatClient,
  type ChatRating,
  type ChatRatingReason,
  type ChatRunBody,
} from '~/services/agent-chat.service';
import { turnsFromMessages } from '~/utils/agent-chat-history';
import {
  applyFinalText,
  chatMessageText,
  isTurnEnded,
  newChatTurn,
  reduceChatTurn,
  resumeChatTurn,
  type ActiveChatRun,
  type ChatIntent,
  type ChatPageContext,
  type ChatRunEvent,
  type ChatTurn,
} from '~/utils/agent-chat';

// A replay can fail as the first stream did. After this many replays in a row without a new
// event, the turn shows the network error and the retry button. The wait before each one doubles.
const MAX_REPLAYS = 3;
const FIRST_REPLAY_WAIT_MS = 1000;

let fallbackId = 0;

const wait = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

/**
 * One conversation with Suge: the session, the turns, and the active run. The panel keeps one
 * instance, so the conversation stays across page changes.
 */
export function useChatConversation(options: {
  /** A getter, so the panel creates the client when the user sends the first question. */
  client: () => AgentChatClient;
  context: () => ChatPageContext | undefined;
  /** A run ended with an answer or a refusal. The header shows the unread dot when the panel is closed. */
  onAnswer?: (turn: ChatTurn) => void;
  /** Where the run of the active turn stays across a page reload. */
  activeRun?: {
    read: () => ActiveChatRun | null;
    write: (run: ActiveChatRun) => void;
    clear: () => void;
  };
  newId?: () => string;
  sleep?: (ms: number) => Promise<void>;
}) {
  const newId = options.newId ?? (() => `turn-${++fallbackId}`);
  const sleep = options.sleep ?? wait;
  const turns = ref<ChatTurn[]>([]);
  const sessionId = ref<string | null>(null);
  /** The ticket that the user created from this conversation. */
  const ticketUid = ref<string | null>(null);
  const activeId = ref<string | null>(null);
  let abort: AbortController | null = null;
  // The user pressed stop before the first event gave the run id.
  let stopRequested = false;
  let disposed = false;

  const busy = computed(() => activeId.value !== null);

  const indexOf = (id: string) => turns.value.findIndex(turn => turn.id === id);
  const update = (id: string, change: (turn: ChatTurn) => ChatTurn) => {
    const index = indexOf(id);
    if (index !== -1) turns.value[index] = change(turns.value[index]);
  };
  const current = (id: string) => turns.value[indexOf(id)];

  const fail = (id: string, error: unknown) => {
    const info =
      error instanceof AgentChatError
        ? { code: error.code, message: error.message, status: error.status }
        : { code: 'unknown', message: '', status: 0 };
    update(id, turn => ({ ...turn, status: 'failed', error: info }));
  };

  const cancelQuietly = async (runId: string) => {
    try {
      await options.client().cancelRun(runId);
    } catch {
      // The stream then ends with the result of the run, and the turn shows it.
    }
  };

  /** Makes the turn the active one. The first event of a new run stores the run for a page reload. */
  const start = (id: string) => {
    activeId.value = id;
    stopRequested = false;
    abort = new AbortController();
    const onEvent = (event: ChatRunEvent) => {
      const known = current(id)?.runId;
      update(id, turn => reduceChatTurn(turn, event));
      if (known) return;
      const intent = current(id)?.intent;
      if (sessionId.value !== null && intent) {
        options.activeRun?.write({ sessionId: sessionId.value, runId: event.runId, intent });
      }
      // agent-api creates the run before its first event. Only then can the client cancel it.
      if (stopRequested) void cancelQuietly(event.runId);
    };
    return { signal: abort.signal, client: options.client(), onEvent };
  };

  const run = async (id: string, body: ChatRunBody) => {
    const { signal, client, onEvent } = start(id);
    try {
      if (sessionId.value === null) {
        const created = await client.createSession();
        // A new chat during the request must not get the session of this run.
        if (signal.aborted) return finish(id);
        sessionId.value = created.id;
      }
      const context = options.context();
      await client.streamRun(sessionId.value, { ...body, ...(context && { context }) }, onEvent, signal);
    } catch (error) {
      if (signal.aborted) return finish(id);
      // A run that did not start has nothing to replay.
      if (!current(id)?.runId) {
        fail(id, error);
        return finish(id);
      }
    }
    if (!current(id)?.runId) {
      fail(id, new AgentChatError(0, 'no_run'));
      return finish(id);
    }
    return follow(id, signal, client, onEvent, false);
  };

  /**
   * Replays the run of a turn until it ends, then takes the final text. The run continues in
   * agent-api after a lost connection or a page reload, so the replay can read it to its end.
   */
  const follow = async (
    id: string,
    signal: AbortSignal,
    client: AgentChatClient,
    onEvent: (event: ChatRunEvent) => void,
    replayNow: boolean,
  ) => {
    let lastError: unknown = new AgentChatError(0, 'network');
    let misses = 0;
    let first = true;
    while (!isTurnEnded(current(id)) && !signal.aborted && misses < MAX_REPLAYS) {
      // A lost connection waits before the replay. A page reload has no stream to lose, so it does not.
      if (!(first && replayNow)) await sleep(FIRST_REPLAY_WAIT_MS * 2 ** misses);
      first = false;
      if (signal.aborted) break;
      update(id, resumeChatTurn);
      const before = current(id).lastSeq;
      try {
        await client.replayRun(current(id).runId!, before, onEvent, signal);
      } catch (error) {
        lastError = error;
      }
      misses = current(id).lastSeq === before ? misses + 1 : 0;
    }
    if (signal.aborted) return finish(id);
    if (!isTurnEnded(current(id))) {
      fail(id, lastError);
      return finish(id);
    }

    const ended = current(id);
    if (ended.status === 'completed' && ended.runId) {
      try {
        const text = await client.getRunText(ended.runId);
        update(id, turn => applyFinalText(turn, text));
      } catch {
        // The streamed text stays. The renderer allows only help links in it.
      }
    }
    return finish(id);
  };

  const finish = (id: string) => {
    if (activeId.value === id) {
      activeId.value = null;
      abort = null;
    }
    const turn = current(id);
    // An aborted reading leaves the run active in agent-api, so a page reload can still resume it.
    if (turn && isTurnEnded(turn)) options.activeRun?.clear();
    if (!disposed && turn && (turn.status === 'completed' || turn.status === 'refused')) options.onAnswer?.(turn);
  };

  const send = async (question: string, intent: ChatIntent = 'answer') => {
    if (busy.value) return;
    actionSeq++;
    const text = chatMessageText(question);
    if (text === '' && intent === 'answer') return;
    const turn = newChatTurn(newId(), text, intent);
    turns.value.push(turn);
    // `answer` is the default intent of agent-api, so the body names only the other intent.
    await run(turn.id, { ...(text !== '' && { message: { text } }), ...(intent !== 'answer' && { intent }) });
  };

  const stop = async () => {
    const id = activeId.value;
    if (id === null) return;
    const runId = current(id)?.runId;
    if (!runId) {
      // The request can still create the run. onEvent cancels it when its id arrives.
      stopRequested = true;
      return;
    }
    await cancelQuietly(runId);
  };

  // Each open, send, and new chat takes a new number. An open applies only if no later action
  // started during its request.
  let actionSeq = 0;

  /** Starts a new chat. The next question creates a new session. */
  const reset = () => {
    actionSeq++;
    if (activeId.value !== null) options.activeRun?.clear();
    abort?.abort();
    abort = null;
    activeId.value = null;
    turns.value = [];
    sessionId.value = null;
    ticketUid.value = null;
  };

  /**
   * Opens a session of the history. A new question continues it. It gives false when it did not
   * change the conversation.
   */
  const open = async (id: string): Promise<boolean> => {
    // The session of the active run is on screen already, and a reload would lose the stream.
    if (busy.value && id === sessionId.value) return false;
    const seq = ++actionSeq;
    const detail = await options.client().getSession(id);
    if (seq !== actionSeq) return false;
    reset();
    sessionId.value = detail.id;
    ticketUid.value = detail.ticket_uid;
    turns.value = turnsFromMessages(detail.messages);
    return true;
  };

  /**
   * Resumes the run that the panel read before a page reload: it opens the session of the run and
   * replays the run from its start, because the new page has none of its events. `opened` runs
   * when the session shows, before the replay.
   */
  const restore = async (opened?: () => void) => {
    const entry = options.activeRun?.read();
    if (!entry || busy.value || sessionId.value !== null || turns.value.length > 0) return;
    try {
      if (!(await open(entry.sessionId)) || disposed) return;
    } catch (error) {
      // A 4xx status does not change on a later reload, for example a session that is gone. A
      // network error or a 5xx status keeps the entry for the next mount.
      const status = error instanceof AgentChatError ? error.status : 0;
      if (status >= 400 && status < 500) options.activeRun?.clear();
      return;
    }
    const index = turns.value.findLastIndex(turn => turn.runId === entry.runId);
    if (index !== -1 && turns.value[index].status === 'completed') {
      options.activeRun?.clear();
      opened?.();
      return;
    }
    const turn: ChatTurn = {
      ...newChatTurn(newId(), index === -1 ? '' : turns.value[index].question, entry.intent),
      runId: entry.runId,
      status: 'streaming',
    };
    if (index === -1) turns.value.push(turn);
    else turns.value[index] = turn;
    options.activeRun?.write(entry);
    opened?.();
    const { signal, client, onEvent } = start(turn.id);
    await follow(turn.id, signal, client, onEvent, true);
  };

  // The turns with a feedback request in flight. A second click waits for no answer and sends nothing.
  const rating = new Set<string>();

  /**
   * Rates an answer. agent-api keeps one rating for each answer, and a new rating replaces the old
   * one. The same rating again sends nothing. A failed request keeps the old rating and throws.
   */
  const rate = async (id: string, value: ChatRating, reason?: ChatRatingReason) => {
    const turn = current(id);
    if (!turn || turn.status !== 'completed' || !turn.messageId || rating.has(id)) return;
    const next = { value, reason: reason ?? null };
    if (turn.rating?.value === next.value && turn.rating.reason === next.reason) return;
    rating.add(id);
    try {
      await options.client().sendFeedback(turn.messageId, value, reason);
      update(id, latest => ({ ...latest, rating: next }));
    } finally {
      rating.delete(id);
    }
  };

  /** Stops the reading of the active run, for example when the panel unmounts. The run continues in agent-api. */
  const dispose = () => {
    disposed = true;
    abort?.abort();
  };

  /** Asks the question of an ended turn again, in place of that turn. */
  const retry = async (id: string) => {
    const turn = current(id);
    if (!turn || busy.value || !isTurnEnded(turn)) return;
    turns.value.splice(indexOf(id), 1);
    await send(turn.question, turn.intent);
  };

  return { turns, sessionId, ticketUid, busy, send, stop, retry, dispose, reset, open, restore, rate };
}
